import React from 'react';
import { ShoppingBag, ExternalLink } from 'lucide-react';

/* Products behind your videos — derived ONLY from the product link the scraper
   already attached to each video. No extra API calls. */

const API = process.env.REACT_APP_BACKEND_URL;

/* TikTok cover/avatar images come back as signed .heic URLs that browsers can't
   render. Route them through the backend image proxy which converts to JPEG. */
export const proxyImg = (url) => {
  if (!url) return '';
  const u = String(url);
  if (/tiktokcdn|byteimg|muscdn|\.heic/i.test(u)) {
    return `${API}/api/img?u=${encodeURIComponent(u)}`;
  }
  return u;
};

export const productLabel = (p) =>
  (p.product_title && p.product_title.trim())
    ? p.product_title.trim()
    : (p.product_id ? `Product ·${p.product_id.slice(-6)}` : 'Shop product');

// Group a video list into distinct products, counted and sorted by most-posted.
export function buildProductSummary(videos = []) {
  const map = new Map();
  for (const v of videos) {
    const link = v.product_link;
    if (!link) continue;
    const key = v.product_id || link;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      if (!existing.product_title && v.product_title) existing.product_title = v.product_title;
    } else {
      map.set(key, {
        key,
        product_id: v.product_id || '',
        product_link: link,
        product_title: v.product_title || '',
        count: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

// Small per-video "Shop" badge (cover overlay). `offset` stacks it below the AD badge.
export const ShopBadge = ({ offset = false, testid }) => (
  <span
    className={`absolute left-1 ${offset ? 'top-7' : 'top-1'} inline-flex items-center gap-0.5 bg-[#1E7A6B] text-white text-[9px] font-bold tracking-wide rounded px-1.5 py-0.5 shadow-sm`}
    title="Shop video — has a product attached"
    data-testid={testid}
  >
    <ShoppingBag className="w-2.5 h-2.5" /> Shop
  </span>
);

// Tiny "View product" link for a video tile footer.
export const ViewProductLink = ({ link, testid }) => (
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    className="mt-1 w-full inline-flex items-center justify-center gap-1 h-6 rounded-md border border-[#CDE6DF] bg-[#EEF7F4] text-[9px] font-semibold text-[#1E7A6B] hover:bg-[#E0F0EB] transition-colors"
    data-testid={testid}
  >
    <ExternalLink className="w-3 h-3" /> View product
  </a>
);

// "Products in your videos" summary block — tidy scannable list.
export const ProductsInVideos = ({ videos = [] }) => {
  const products = buildProductSummary(videos);
  const total = products.reduce((n, p) => n + p.count, 0);

  return (
    <div className="mb-5 rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4" data-testid="products-in-videos">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingBag className="w-4 h-4 text-[#1E7A6B]" strokeWidth={1.8} />
        <h3 className="font-display text-base text-[#1F1B16]">Products in your videos</h3>
      </div>
      {products.length === 0 ? (
        <p className="text-xs text-[#9D8E7B]" data-testid="products-in-videos-empty">
          None of your recent videos have a shop product attached, so there's nothing to count yet.
        </p>
      ) : (
        <>
          <p className="text-xs text-[#9D8E7B] mb-3">
            {products.length} product{products.length > 1 ? 's' : ''} across {total} shop video{total > 1 ? 's' : ''}, most-posted first.
          </p>
          <div className="rounded-[12px] border border-[#EFE1D1] bg-white divide-y divide-[#F0E8DB] overflow-hidden" data-testid="products-in-videos-list">
            {products.map((p, i) => (
              <div key={p.key} className="flex items-center gap-3 px-3 py-2" data-testid={`product-summary-row-${i}`}>
                <span className="w-5 text-center font-display text-sm text-[#9D174D] shrink-0">{i + 1}</span>
                <p className="text-sm text-[#1F1B16] font-medium truncate flex-1 min-w-0">{productLabel(p)}</p>
                <span className="text-xs text-[#6F675D] shrink-0 tabular-nums">{p.count} video{p.count > 1 ? 's' : ''}</span>
                <a
                  href={p.product_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E7A6B] hover:underline shrink-0"
                  data-testid={`product-summary-link-${i}`}
                >
                  View <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
